// eslint-disable-next-line @typescript-eslint/triple-slash-reference
///<reference path="../UI/External/JQuery/jquery/jquery.d.ts"/>
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
///<reference path="../UI/External/JQuery/jqueryui/jqueryui.d.ts"/>
/**
 * Interface definujici události pro element tsc-control
 */
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ICompositeComponent } from "UI/UI/UI/Controls/ControlInterfaces";
import { DataConverter } from "../../Global/Base/Converters/DataConverter";
import { CaseInsensitiveMap } from "../../Global/Base/DataTypes/CaseInsensitiveMap";
import { IDisposable } from "../../Global/Base/IDisposable";
import { TimerUtils } from "../../Global/Base/Utils/TimerUtils";
import { EventsController } from "../../Global/Dom/EventsController";
import { ElementUtils } from "../UI.ElementarControls/Helpers/ElementUtils";
import { registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { GenericControl } from "./GenericControl";
import {
    IAttributeChangeEvent,
    IAttributeChangeEventData,
    IBooleanDetailValueEvent,
    IBooleanDetailValueEventData,
    IStringDetailValueEvent,
    IStringDetailValueEventData,
    IWidgetResizableEventTarget,
} from "./IEvent";

export interface Control extends IWidgetResizableEventTarget {
    addEventListener(type: "visibility", listener: (ev: IBooleanDetailValueEvent) => any, options?: boolean | AddEventListenerOptions): void;
    addEventListener(type: "enabled", listener: (ev: IBooleanDetailValueEvent) => any, options?: boolean | AddEventListenerOptions): void;
    addEventListener(type: "canvalueload", listener: (ev: IBooleanDetailValueEvent) => any, options?: boolean | AddEventListenerOptions): void;
    addEventListener(type: "culturechanged", listener: (ev: IStringDetailValueEvent) => any, options?: boolean | AddEventListenerOptions): void;
    addEventListener(type: "attributechange", listener: (ev: IAttributeChangeEvent) => any, options?: boolean | AddEventListenerOptions): void;
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void;
}

const logger = LazyLoadingSectionLogger.create(LoggingSection.control);

export class Control extends HTMLElement implements IDisposable, ICompositeComponent {
    //#region Fields
    private static _initialized: boolean = false;
    private static _staticListeners: CaseInsensitiveMap<Array<IRegisteredStaticEventListener>>;
    private static _styleElement: HTMLStyleElement;
    private static _counterElement: number = 0;

    protected _innerId: string;
    protected _culture: string;
    protected _visible: boolean = true;

    /**
     * Informace o tom, zda se ma na komponente pri odpojeni z DOMu automaticky zavolat dispose (vychozi: true)
     * @default true
     */
    public disposeOnDetach: boolean = true;

    private _listeners: CaseInsensitiveMap<IRegisteredEventListener>;
    private _reregisterListeners: Array<() => void>;
    protected _isAttached: boolean;
    private _widgets: any = {};
    private _afterAttach: Array<(item: Control) => void>;
    private _lastCanValueLoad: boolean = false;
    /**
     * Příznak jestli se jedná o kompozitní komponentu (složenou z dalších komponent)
     * Pozn. Má vliv na vyhodnocení práv. Pro kompozitní komponenty se nevyhodnocují práva na podřízených komponentách
     */
    public isCompositeComponent?: boolean;

    protected _eventsController = new EventsController();
    //#endregion Fields

    //#region Properties
    public static get styleElement(): HTMLStyleElement {
        if (!Control._styleElement) {
            const styleEl = document.head.getElementsByTagName("style");
            if (styleEl.length > 0)
                Control._styleElement = styleEl[0];
            else {
                Control._styleElement = document.createElement("style");
                document.head.appendChild(Control._styleElement);
            }
        }

        return Control._styleElement;
    }

    /**
     * Vrátí true, pokud je dokument připojen do DOMu
     */
    public get isAttached(): boolean {
        return this._isAttached;
    }

    //#region Enabled
    public set enabled(value: boolean) {
        this.setAttribute("enabled", value.toString());

        this.callEvent("enabled", { value: value } as IBooleanDetailValueEventData);
    }

    public get enabled(): boolean {
        const r = this.getAttribute("enabled") as string;
        return DataConverter.toBoolean(r, true);
    }
    //#endregion Enabled

    //#region Visible
    public set visible(value: boolean) {
        const oldValue = this._visible;
        this._visible = value;
        this.classList.toggle("hidden", !value);
        if (value) {
            this.style.display = "";
        } else {
            this.style.display = "none";
        }
        if (oldValue !== value) {
            TimerUtils.waitOrCancel("control_visible" + this._innerId, () => {
                this.callEvent("visibility", { value: value } as IBooleanDetailValueEventData);
            }, 50);
        }
    }

    public get visible(): boolean {
        return this._visible;
    }
    //#endregion Visible

    //#region Culture
    /** Pole obsahujicí lokalizancni kulturu pro nastaveni formatovani aj. pouze pro dany kontrol, pokud je undefined je pouzita vychozi */
    public get culture(): string {
        return this._culture;
    }

    public set culture(value: string) {
        this._culture = value;

        this.callEvent("culturechanged", { value: value } as IStringDetailValueEventData);
    }
    //#endregion Culture

    /**
     * Vraci informace o tom, zda je mozne nacitat hodnotu (zda je objekt viditelny z pohledu práv)
     */
    public get canValueLoad(): boolean {
        if (this.visible) {
            return GenericControl.allParents(this as HTMLElement, (el: HTMLElement) => {
                if (el instanceof Control)
                    return (el as Control).visible;
                if (el.style.display === "none")
                    return false;
                else
                    return true;
            });
        } else
            return false;
    }

    protected static get observedAttributes(): string[] {
        return ["culture"];
    }
    //#endregion Properties

    //#region Methods
    private static _initStatic(): void {
        if (Control._initialized)
            return;
        Control._initialized = true;
        Control._staticListeners = new CaseInsensitiveMap<Array<IRegisteredStaticEventListener>>();
    }

    public afterAttach(fnc: (item: Control) => void): void {
        this._afterAttach.push(fnc);

        if (this.isAttached)
            fnc(this);
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        this._listeners = new CaseInsensitiveMap<IRegisteredEventListener>();
        this._reregisterListeners = [];
        this._afterAttach = [];
        this._innerId = Control.getID();
        this._visible = true;
        this._widgets = {
            dispose: [],
        };

        this.classList.add("CustomControl");

        this._isAttached = false;
        Control._initStatic();

        this.registerEventListenerWithReregister("visibility", () => {
            const val = this.canValueLoad;
            if (val === this._lastCanValueLoad)
                return;

            this._lastCanValueLoad = val;
            this.callEvent("canvalueload", { value: val } as IBooleanDetailValueEventData);

            // vyvolani události pro podrizene prvky
            const EL_LIST = Array.from(this.children as HTMLCollection) as HTMLElement[];

            for (const htc of EL_LIST) {
                const htControl = htc as Control;
                if (htControl && htControl instanceof Control) {
                    htControl.callEvent("canvalueload", { value: val } as IBooleanDetailValueEventData);
                }
            }
        });
    }

    // You can also define the other lifecycle methods.
    protected connectedCallback(): void {
        this._isAttached = true;

        const reregisterEvents = this._reregisterListeners;
        this._reregisterListeners = [];
        for (const f of reregisterEvents) f();

        for (const f of this._afterAttach) {
            f(this);
        }
        this.callEvent("attached");
    }

    protected disconnectedCallback(): void {
        this._isAttached = false;
        if (this.disposeOnDetach) this.dispose();
        this.callEvent("detached");
    }

    protected attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        this.callEvent("attributechange", {
            attrName: attrName,
            oldVal: oldVal,
            newVal: newVal,
        } as IAttributeChangeEventData);

        switch (attrName) {
            case "culture":
                this.culture = newVal;
                break;
        }
    }

    /**
     * Metoda, která vrací true nebo false, na základě fyzické viditelnosti prvku
     */
    public static isElementVisible(element: HTMLElement): boolean {
        return GenericControl.isElementVisible(element);
    }

    /**
     * Metoda, která vrací true nebo false, na základě fyzické viditelnosti prvku (prohleda i nadrazene prvky, jestli jsou skryte)
     */
    public isVisible(): boolean {
        return Control.isElementVisible(this);
    }

    //#region Widgets
    //#region Swipe widget
    /**
     * Aktivuje udalosti pro swipe
     */
    public widgetTouchSwipe(status: boolean = true): void {
        this.unregisterEventListenerById("widget_swipe_touchstart");
        this.unregisterEventListenerById("widget_swipe_touchmove");
        this.unregisterEventListenerById("widget_swipe_touchend");

        if (!status)
            return;

        if (!this._widgets.touchSwipe) {
            this._widgets.touchSwipe = {
                registered: true,
            };
            this._widgets.dispose.push(() => {
                this.unregisterEventListenerById("widget_swipe_touchstart");
                this.unregisterEventListenerById("widget_swipe_touchmove");
                this.unregisterEventListenerById("widget_swipe_touchend");
            });
        }

        let swipedir: string,
            startX: number,
            startY: number,
            distX: number,
            distY: number;
        const threshold = 150, //minimalni vzdalenost pro vyvolani swipe
            restraint = 100, // maximalni vzdalenost v kolmem smeru
            allowedTime = 300; // maximalni dobra provadeni pohybu aby se to bralo jako swipe
        let elapsedTime: number,
            startTime: number;

        this.registerEventListener("touchstart", (e: TouchEvent) => {
            const touchobj = e.changedTouches[0];
            swipedir = "none";
            distX = distY = 0;
            startX = touchobj.pageX;
            startY = touchobj.pageY;
            startTime = new Date().getTime(); // cas prvniho dotyku
            //e.preventDefault();
        }, "widget_swipe_touchstart");

        this.registerEventListener("touchmove", (_e: TouchEvent) => {
            //e.preventDefault();
        }, "widget_swipe_touchmove");

        this.registerEventListener("touchend", (e: TouchEvent) => {
            const touchobj = e.changedTouches[0];
            distX = touchobj.pageX - startX;
            distY = touchobj.pageY - startY;
            elapsedTime = new Date().getTime() - startTime;
            if (elapsedTime <= allowedTime) {
                if (Math.abs(distX) >= threshold && Math.abs(distY) <= restraint) {
                    swipedir = (distX < 0) ? "left" : "right"; // pokud je distX negativni je to levy swipe
                } else if (Math.abs(distY) >= threshold && Math.abs(distX) <= restraint) {
                    swipedir = (distY < 0) ? "up" : "down"; // pokud je distY negativni je to swipe nahoru
                }
            }

            this.callEvent("widgetSwipe", {
                direction: swipedir,
            });

            //e.preventDefault();
        }, "widget_swipe_touchend");
    }
    //#endregion Swipe widget

    //#region Sortable widget
    /**
     * Nastavi elementu, zda je mozne jej rozsirovat
     * @param status Parametr obsahujici aktivaci nebo deaktivaci resizovani
     * @param options
     */
    public widgetSortable(status: boolean = true, options: IControlSortableOptions = {}): void {
        this._sortableRegisterDefaultVars();
        if (!status) {
            if (!this._widgets.sortable.isSortable)
                return;
            this._widgets.sortable.isSortable = false;

            $(this).sortable("destroy");
            //this.sortableRegisterDefaultVars();
        } else {
            if (this._widgets.sortable.isSortable)
                return;
            this._widgets.sortable.isSortable = true;
            //this.sortableOptionsParse(options);
            $(this).sortable({
                helper: "clone", // Zabraňuje spuštění dalších událostí (click)
                start: (_e: JQueryEventObject, ui: JQueryUI.SortableUIParams) => {
                    ui.item.oldIndex = ui.item.index(); // Umožní zjistit předchozí pozici
                    this.callEvent("sortablestart", {
                        element: ui.item[0],
                        placeholder: ui.placeholder[0],
                    }, true);
                },
                sort: (_e: JQueryEventObject, ui: JQueryUI.SortableUIParams) => {
                    this.callEvent("sortabledrag", {
                        element: ui.item[0],
                    });
                },
                stop: (_e: JQueryEventObject, ui: JQueryUI.SortableUIParams) => {
                    //logger.log(e, ui);
                    this.callEvent("sortablestop", {
                        element: ui.item[0],
                        ui: ui,
                    }, true);
                },
                scroll: true,
                items: options.items || "> *:not(:first)",
                axis: options.axis,
                distance: options.distance,
                handle: options.handle || this,
            });
        }
    }

    private _sortableRegisterDefaultVars(): void {
        if (!this._widgets.sortable) {
            //this.widgets.dispose.push(() => this.sortableUnregisterListeners());
            this._widgets.sortable = {
                isSorting: false,
                isSortable: false,
                items: [],
                placeholderElement: null,
            };
        }
    }

    //private sortableOptionsParse(options: IControlSortableOptions = {}) {
    //    this.classList.add("sortableWidget");

    //    this.widgets.sortable.items = Array.from(document.querySelectorAll(`:scope ${options.items ? options.items : "> *"}`));

    //    for (let ch of this.widgets.sortable.items) {
    //        const handleElement = options.handle ? ch.querySelector(`:scope ${options.handle}`) : ch;
    //        ch.classList.add("sortableItem");
    //        handleElement.classList.add("sortable-handle");

    //        Control.registerEventListener("sortableWidget", handleElement, "mousedown", (e: MouseEvent) => {
    //            e.stopPropagation();
    //            this.sortableMouseStart(e, options);
    //        });
    //    }
    //}

    //private sortableUnregisterListeners() {
    //    Control.unregisterEventListener("sortableWidget");

    //    this.classList.remove("sortableWidget");
    //}

    //#endregion Sortable widget

    //#region Resizable Widget
    /**
     * Nastavi elementu, zda je mozne jej rozsirovat
     * @param status Parametr obsahujici aktivaci nebo deaktivaci resizovani
     * @param options
     */
    public widgetResizable(status: boolean = true, options: IControlResizableOptions = {}): void {
        this._resizableRegisterDefaultVars();
        if (!status) {
            if (!this._widgets.resizable.isResizable)
                return;
            this._widgets.resizable.isResizable = false;
            this._resizableUnregisterListeners();
        } else {
            if (this._widgets.resizable.isResizable && this.classList.contains("resizableWidget"))
                return;
            this._widgets.resizable.isResizable = true;
            this._resizableOptionsParse(options);
        }
    }

    private _resizableRegisterDefaultVars(): void {
        if (!this._widgets.resizable) {
            //this.widgets.dispose.push(() => this.resizableUnregisterListeners());
            this._widgets.resizable = {
                isResizing: false,
                isResizable: false,
                resizingDirection: "x",
                resizeXElement: null,
                resizeYElement: null,
                resizeXYElement: null,
            };
        }
    }

    private _resizableOptionsParse(options: IControlResizableOptions = {}): void {
        this.classList.add("resizableWidget");

        let resizeX = options.handles && options.handles.x;
        const resizeY = options.handles && options.handles.y;
        const resizeXY = options.handles && options.handles.xy;

        if (!resizeX && !resizeY)
            resizeX = true;

        if (resizeX) {
            this._widgets.resizable.resizeXElement = document.createElement("div");
            this._widgets.resizable.resizeXElement.classList.add("resizableHandle", "resizeHorizontal");

            this._eventsController.addListener(this._widgets.resizable.resizeXElement, "mousedown", (e: MouseEvent) => {
                e.stopPropagation();
                this._widgets.resizable.resizingDirection = "x";
                this._widgets.resizable.isResizing = true;
                this._resizableMouseStart(e, options);
            });

            this._eventsController.addListener(this._widgets.resizable.resizeXElement, "click", (e: MouseEvent) => {
                e.stopPropagation();
            });
            this.appendChild(this._widgets.resizable.resizeXElement);
        }

        if (resizeY) {
            this._widgets.resizable.resizeYElement = document.createElement("div");
            this._widgets.resizable.resizeYElement.classList.add("resizableHandle", "resizeVertical");

            this._eventsController.addListener(this._widgets.resizable.resizeYElement, "mousedown", (e: MouseEvent) => {
                e.stopPropagation();
                this._widgets.resizable.isResizing = true;
                this._widgets.resizable.resizingDirection = "y";
                this._resizableMouseStart(e, options);
            });
            this._eventsController.addListener(this._widgets.resizable.resizeYElement, "click", (e: MouseEvent) => {
                e.stopPropagation();
            });
            this.appendChild(this._widgets.resizable.resizeYElement);
        }

        if (resizeXY) {
            this._widgets.resizable.resizeXYElement = document.createElement("div");
            this._widgets.resizable.resizeXYElement.classList.add("resizableHandle", "resizeHorizontalVertical");

            this._eventsController.addListener(this._widgets.resizable.resizeXYElement, "mousedown", (e: MouseEvent) => {
                e.stopPropagation();
                this._widgets.resizable.isResizing = true;
                this._widgets.resizable.resizingDirection = "xy";
                this._resizableMouseStart(e, options);
            });
            this._eventsController.addListener(this._widgets.resizable.resizeXYElement, "click", (e: MouseEvent) => {
                e.stopPropagation();
            });
            this.appendChild(this._widgets.resizable.resizeXYElement);
        }

        //this.registerEventListener("mousedown", (e) => {
        //    this.isResizing = true;
        //}, "resizableWidgetMouseDown");

        this._eventsController.addListener(window, "mouseup", (_e: MouseEvent) => {
            if (!this._widgets.resizable.isResizing)
                return;
            this._widgets.resizable.isResizing = false;
            this.classList.remove("resizable-resizing");
            this.callEvent("resizablestop", undefined, true);
        });

        this._eventsController.addListener(window, "mousemove", (e: MouseEvent) => {
            if (!this._widgets.resizable.isResizing)
                return;
            e.stopPropagation();
            this._resizableMouseDrag(e, options);
        }, { options: { passive: true } });
    }

    private _resizableMouseStart(event: MouseEvent, options: IControlResizableOptions): void {
        let curLeft: number, curTop: number;
        const o = options;
        if (!o.containment)
            o.containment = this.parentElement;

        this._widgets.resizable.isResizing = true;

        this._widgets.resizable.startOffset = this.offset();

        curLeft = this._widgets.resizable.startOffset.left;
        curTop = this._widgets.resizable.startOffset.top;

        if (o.containment) {
            curLeft += o.containment.scrollLeft || 0;
            curTop += o.containment.scrollTop || 0;
        }

        this._widgets.resizable.size = {
            width: this.offsetWidth,
            height: this.offsetHeight,
        };

        this._widgets.resizable.originalSize = {
            width: this.offsetWidth,
            height: this.offsetHeight,
        };

        this._widgets.resizable.originalPosition = { left: curLeft, top: curTop };
        this._widgets.resizable.originalMousePosition = { left: event.pageX, top: event.pageY };

        this.classList.add("resizable-resizing");
        this.callEvent("resizablestart");
    }

    private _resizableMouseDrag(event: MouseEvent, _options: IControlResizableOptions): void {
        if (this._widgets.resizable.isResizing) {
            if (this._widgets.resizable.resizingDirection === "x" || this._widgets.resizable.resizingDirection === "xy") {
                this.style.width = `${this._widgets.resizable.size.width + event.movementX}px`;
                this._widgets.resizable.size.width = this._widgets.resizable.size.width + event.movementX;
            }
            if (this._widgets.resizable.resizingDirection === "y" || this._widgets.resizable.resizingDirection === "xy") {
                this.style.height = `${this._widgets.resizable.size.height + event.movementY}px`;
                this._widgets.resizable.size.height = this._widgets.resizable.size.height + event.movementY;
            }
            this.callEvent("resizabledrag", this._widgets.resizable.size);
        }
    }

    private _resizableUnregisterListeners(): void {
        this.unregisterEventListenerById("resizableWidgetMouseDown");
        Control.unregisterEventListener("resizableWidget");

        this.classList.remove("resizableWidget");
        if (this._widgets.resizable.resizeYElement)
            this._widgets.resizable.resizeYElement.remove();
        if (this._widgets.resizable.resizeXElement)
            this._widgets.resizable.resizeXElement.remove();
        if (this._widgets.resizable.resizeXYElement)
            this._widgets.resizable.resizeXYElement.remove();
    }
    //#endregion Resizable Widget
    //#endregion

    /**
     * Vrátí pozici elementu v rámci dokumentu
     */
    public offset(): { top: number; left: number } {
        return ElementUtils.offset(this);
    }

    public static getID(): string {
        if (!Control._counterElement)
            Control._counterElement = 0;
        return `_id_control_${Control._counterElement++}`;
    }

    private _registerEventListener(event: string, listener: EventListener, options?: IRegisterListenerOptions): string {
        const listenerName = options?.customId ?? `${Control.getID()}_${event}`;

        if (options?.customId && this._listeners.has(options?.customId))
            throw Error("Id has been registered");

        this._listeners.set(listenerName, {
            action: listener,
            event: event,
            options: {
                ...options,
                reregister: options?.reregister ?? false,
                once: options?.once ?? false,
            },
        });

        if (options?.once) {
            const F_CALL = (evt: Event) => {
                this.removeEventListener(event, F_CALL);
                this._listeners.delete(listenerName);
                listener(evt);
            };
            this.addEventListener(event, F_CALL, options?.options);
        } else {
            this.addEventListener(event, listener, options?.options);
        }

        return listenerName;
    }

    /**
     * Zaregistruje listener události, který je po detachi automaticky odregistrován
     * @param event Název události
     * @param listener Akce při vyvolání události
     * @param withReregister Pokud je nastaveno na true tak je po opětovném připojení znovu zaregistrovan
     */
    public registerEventListener(event: string, listener: EventListener, customId?: string, options?: boolean | AddEventListenerOptions): void {
        this._registerEventListener(event, listener, {
            customId: customId,
            options: options,
            reregister: false,
        });
    }

    /**
     * Událost, které je zavolána pouze jednou potom je odregistrována
     * @param event Název události
     * @param listener Akce při vyvolání události
     */
    public registerEventListenerOnce(event: string, listener: EventListener): void {
        this._registerEventListener(event, listener, { once: true });
    }

    /**
     * Zaregistruje listener události, který je po detachi automaticky odregistrován a při opětovném attachi znovu zaregistrován
     * @param event Název události
     * @param listener Akce při vyvolání události
     */
    public registerEventListenerWithReregister(event: string, listener: EventListener, options?: boolean | AddEventListenerOptions): void {
        if (this.isAttached) {
            this._registerEventListener(event, listener, {
                reregister: true,
                options: options,
            });
        } else {
            this._reregisterListeners.push(() => {
                this._registerEventListener(event, listener, {
                    reregister: true,
                    options: options,
                });
            });
        }
    }

    /**
     * Zaregistruje listener do skupiny událostí, tyto listenery jsou po detachi automaticky odregistrovány
     * @param groupId ID skupiny udalosti
     * @param element Element pro ktery se bude udalost registrovat
     * @param event Nazev udalosti
     * @param action Akce vyvolana pri udalosti
     */
    public static registerEventListener(groupId: string, element: Element | Document, event: string, action: (e: Event) => void): void {
        let data: Array<IRegisteredStaticEventListener>;
        if (Control._staticListeners.has(groupId))
            data = Control._staticListeners.get(groupId);
        else
            data = [];

        data.push({
            action: action,
            event: event,
            element: element,
            options: {
                reregister: false,
                once: false,
            },
        });

        Control._staticListeners.set(groupId, data);
        element.addEventListener(event, action);
    }

    /**
     * Odregistruje akce pro danou skupinu
     * @param groupId Identifikator skupiny
     * @param element Pokud chceme odebrat pro konkretni element
     * @param event Pokud chceme v elementu odebrat pro konkretni udalost
     * @param action Pokud chceme v udalosti odebrat pro konkretní akci
     */
    public static unregisterEventListener(groupId: string, element?: Element | Document, event?: string, action?: (e: Event) => void): void {
        if (!groupId)
            throw Error("Undefined groupId parameter");
        if (!Control._staticListeners.has(groupId))
            return;

        const data = Control._staticListeners.get(groupId);
        const newData: Array<IRegisteredStaticEventListener> = [];
        const allDelete = !element && !event && !action;

        for (const d of data) {
            if (allDelete) {
                d.element.removeEventListener(d.event, d.action);
            } else {
                if (element) {
                    if (element == d.element) {
                        if (event) {
                            if (event === d.event) {
                                if (action) {
                                    if (action == d.action) {
                                        d.element.removeEventListener(d.event, d.action);
                                    } else
                                        newData.push(d);
                                } else
                                    d.element.removeEventListener(d.event, d.action);
                            } else
                                newData.push(d);
                        } else
                            d.element.removeEventListener(d.event, d.action);
                    } else {
                        newData.push(d);
                    }
                } else {
                    d.element.removeEventListener(d.event, d.action);
                }
            }
        }

        if (newData.length === 0)
            Control._staticListeners.delete(groupId);
        else if (newData.length !== data.length)
            Control._staticListeners.set(groupId, newData);
    }

    /**
     * Odregistruje udalost. Pokud neni vyplnen zadny parametr, jsou odregistrovany vsechny udalosti. Pokud je vyplnen pouze event, odregistruje vsechny udalosti daneho typu
     * pokud je vyplnen event a action, odregistruje pouze konkretni akci
     * @param event Event, ktery chcete odregistrovat.
     * @param action Akce, ktere se tyka event a chcete ji odregistrovat
     */
    public unregisterEventListener(event?: string, action?: (e: Event) => void): void {
        const F_DEL = (key: string, data: IRegisteredEventListener) => {
            this.removeEventListener(data.event, data.action);
            const d = this._listeners.get(key);
            if (d.options.reregister) {
                this._reregisterListeners.push(() => {
                    this._registerEventListener(d.event, d.action, d);
                });
            }
            this._listeners.delete(key);
        };

        for (const [key, data] of this._listeners.entries()) {
            if (event) {
                if (event === data.event) {
                    if (action) {
                        if (action === data.action)
                            F_DEL(key, data);
                    } else
                        F_DEL(key, data);
                }
            } else
                F_DEL(key, data);
        }
    }

    /**
     * Vrátí nalezené eventlistenery pro danou akci
     * @param event Event, ktery chcete vyhledat.
     */
    public getEventListeners(event: string): Array<(e: Event) => void> {
        const listeners: Array<(e: Event) => void> = [];
        for (const e of this._listeners) {
            if (event === e[1].event)
                listeners.push(e[1].action);
        }

        return listeners;
    }

    /**
     * Odebere listener události na základě customId
     * @param customId Custom id události
     */
    public unregisterEventListenerById(customId: string): void {
        if (this._listeners.has(customId)) {
            const ev = this._listeners.get(customId);
            this.removeEventListener(ev.event, ev.action);
            this._listeners.delete(customId);
        }
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @param bubbles Zda má událost probublávat DOMem nebo ne
     * @param cancelable Zda může být událost zrušena a zda může být voláno preventDefault()
     * @return Vrátí customEvent
     */
    public callEvent<DetailType>(eventName: string, detail?: DetailType, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        return ElementUtils.callEvent(this, eventName, detail, bubbles, cancelable);
    }
    //#endregion Methods

    public dispose(): void {
        this.unregisterEventListener();

        if (this._widgets.dispose && Array.isArray(this._widgets.dispose)) {
            for (const f of this._widgets.dispose) {
                try {
                    f();
                } catch (e) {
                    logger.error("dispose function error");
                }
            }
        }

        this._eventsController?.dispose();
    }
}

export type IRegisterListenerOptions = {
    customId?: string;
    /** Bude znova zaregistrovan po připojeni do DOM */
    reregister?: boolean;
    options?: boolean | AddEventListenerOptions;
    /** Bude zavolan pouze jednou */
    once?: boolean;
};

export interface IRegisteredEventListener {
    action: (e: Event) => void;
    event: string;
    options: IRegisterListenerOptions;
}

export interface IRegisteredStaticEventListener extends IRegisteredEventListener {
    element: Element | Document;
}

//#region Widget Options
export interface IControlResizableOptions {
    handles?: {
        x?: boolean; // ve vychozim stavu povoleno, jinak povoleni resizovani v ose xy
        y?: boolean; // povoleni resizovani v ose y
        xy: boolean; // povoleni resizovani v ose x/y soucasne
    };
    containment?: HTMLElement;
}

export interface IControlSortableOptions {
    handle?: string; //selector
    items?: string; //selector
    containment?: HTMLElement;
    distance?: number; // pocet pixelu nez zacne trideni
    axis?: string;
}
//#endregion Widget Options

registerCustomElement("tsc-control", Control);
